import Portal from 'preact-portal'
import flowtip from 'flowtip-core'
import {h, Component, ComponentChildren} from 'preact'
import CSSTransitionGroup from 'preact-css-transition-group'
import { isHtmlElement } from '../../libs/typeGuards'

const showDelay = 70
const hideDelay = 300
const showIndex = 5225  // 5000 -> popup
const initialIndex = 5224

interface Props {
  hoverControl: HTMLElement,
  triggerOnClick?: boolean,
  children: ComponentChildren,
  popupClass?: string,
  hideOnClickInside?: boolean,
  region?: 'left' | 'right' | 'top' | 'bottom',
  align?: 'start' | 'center' | 'end' | number,
  offset?: number,
  onToggle?: (isVisible: boolean) => void,
  size: {
    width: number,
    height: number
  },
}

interface State {
  show: boolean,
  zIndex: number,
  left: number,
  top: number,
}

class Popup extends Component<Props, State> {
  private hideTimeout: number = 0
  private showTimeout: number = 0

  constructor (props: Props) {
    super(props)
    if (props.triggerOnClick) {
      props.hoverControl.addEventListener('click', e => {
        e.preventDefault()
        this.show()
      })
      document.addEventListener('click', (e) => {
        let popup = document.body.querySelector(`.${this.props.popupClass || 'popup-component'}`)
        if (!this.state.show) return
        if (props.hideOnClickInside) return this.hide(0)
        if (!popup) return
        if (!isHtmlElement(e.target)) return
        if (popup.contains(e.target)) return
        this.hide(0)
      })
    } else {
      try {
        props.hoverControl?.addEventListener('mouseover', () => this.show())
        props.hoverControl?.addEventListener('mouseout', () => this.hide())
      } catch {}
    }

    this.state = {
      zIndex: initialIndex,
      show: false,
      top: 0,
      left: 0,
    }
  }

  show () {
    clearTimeout(this.hideTimeout)
    if (this.state.show) return
    this.showTimeout = setTimeout(() => {
      if (this.state.show) return
      if (this.props.onToggle) this.props.onToggle(true)
      this.calculateMetrics(this.props)
    }, showDelay)
  }

  calculateMetrics (props: Props) {
    const bodyRect = document.body.getBoundingClientRect()
    const bounds =  {
      left: 0,
      top: 0,
      width: document.documentElement.clientWidth - 15,
      height: document.documentElement.clientHeight - 15
    }
    const targetRect = this.props.hoverControl.getBoundingClientRect()
    const result = flowtip({
      target: targetRect,
      content: props.size,
      bounds: bounds,
      offset: props.offset,
      disabled: {
        left: true,
        right: true
      },
      region: props.region || 'bottom',
      align: props.align
    })
    this.setState({
      show: true,
      left: Math.floor(result.rect.left - bodyRect.left),
      top: Math.floor(result.rect.top - bodyRect.top),
      zIndex: showIndex
    })
  }

  componentWillReceiveProps(nextProps: Props) {
    if (!this.state.show) return
    if (nextProps.size.width === this.props.size.width &&
        nextProps.size.height === this.props.size.height) return
    this.calculateMetrics(nextProps)
  }

  hide (delay = hideDelay) {
    clearTimeout(this.hideTimeout)
    clearTimeout(this.showTimeout)
    this.setState({ zIndex: initialIndex })
    this.hideTimeout = setTimeout(() =>  {
      if (this.props.onToggle) this.props.onToggle(false)
      this.setState({ show: false })
    }, delay)
  }

  toggle (on: boolean) {
    if (!this.props.triggerOnClick) {
      on ? this.show() : this.hide()
    }
  }

  render ({ children }: Props, { show, left, top, zIndex }: State) {
    return (
      <Portal into={'body'}>
        <CSSTransitionGroup transitionName='popup-transition' transitionEnterTimeout={150} transitionLeaveTimeout={150}>
          {show
            ? <div class={this.props.popupClass || 'popup-component'}
              onMouseLeave={() => {
                this.toggle(false)
              }}
              onMouseEnter={() => {
                this.toggle(true)
              }}
              style={{
                left,
                top,
                opacity: this.props.size.width ? null : 0,
                zIndex: zIndex
              }} >
              {children}
            </div>
            : null}
        </CSSTransitionGroup>
      </Portal>
    )
  }
}

export default Popup
